/**
 * The one place that reconciles this app's `renderOrder` ladder with the
 * renderer's **reversed-Z depth buffer**.
 *
 * ## The trap
 *
 * `flight-view.ts` runs `reversedDepthBuffer: true`, and three's shared render
 * list compensates for the inverted clip-space z by reversing the whole sorted
 * array (`three/src/renderers/common/RenderList.js`):
 *
 * ```js
 * if ( this.transparent.length > 1 ) this.transparent.sort( customTransparentSort || reversePainterSortStable );
 * if ( reversedDepth ) { this.opaque.reverse(); this.transparentDoublePass.reverse(); this.transparent.reverse(); }
 * ```
 *
 * `reversePainterSortStable` keys on `groupOrder`, then **`renderOrder`**, then
 * `z`, then `id`. Reversing the array to fix the *z* term therefore flips the
 * `renderOrder` term with it, and **every `renderOrder` in this codebase means
 * the opposite of what it says**. Measured against three's own `RenderList`,
 * with the flight scene's actual constants:
 *
 * | `reversedDepth` | draw order (first -> last) |
 * | --- | --- |
 * | `false` | prop disc (1) -> searchlights (3) -> tracers (4) -> dash quad (5) |
 * | `true` | dash quad (5) -> tracers (4) -> searchlights (3) -> prop disc (1) |
 *
 * The dashboard quad carries `depthWrite: false` (it is mostly transparent, and
 * a plane half a metre in front of the pilot that wrote depth would erase the
 * sky). So when it is drawn **first**, it leaves no depth for anything else's
 * `depthTest` to be rejected by, and every transparent thing in the scene
 * composites straight over the instrument panel. That is the user's
 * 2026-08-26 report — *"bullets are rendering in front of the cockpit panel"* —
 * and the same inversion is why `DASH_QUAD_RENDER_ORDER = 5` did not actually
 * settle the propeller disc it was raised for.
 *
 * ## Why nobody saw it
 *
 * Every capture the 3D COCKPIT lane verified against was taken through
 * `/fly?renderer=webgl` (`cockpit-3d-design.md` §11-12), and three's WebGL
 * fallback **silently turns reversed depth off** when `EXT_clip_control` is
 * missing (`webgl-fallback/WebGLBackend.js`: *"Unable to use reversed depth
 * buffer ... Fallback to default depth buffer"*). So the capture hatch renders
 * the ladder as declared and the game renders it upside down. This is the
 * `lessons.md` rule about offscreen harnesses restated for a backend pin: a
 * capture path that does not reproduce the swapchain's depth configuration is a
 * control for the arithmetic, never for the picture.
 *
 * ## The rule
 *
 * Fix the sort, not the numbers. Negating the constants at each site would
 * scatter the renderer's quirk across nine files and read as nonsense
 * ("the dashboard draws last, so give it the lowest number"); overriding the
 * comparator states it once, where the renderer is built, and leaves every
 * `renderOrder` in the scene meaning what its own doc comment says.
 *
 * `reversedDepthTransparentSort` is three's `reversePainterSortStable` with the
 * two **authored** ordering keys — `groupOrder` and `renderOrder` — negated, and
 * the `z` and `id` terms left exactly as three wrote them. That distinction is
 * the whole safety argument: the array is still reversed afterwards, so the
 * depth term ends up in precisely the order three's reversed-Z path already
 * produced, and this override cannot change which of two co-planar transparent
 * surfaces wins. It only restores the ladder.
 *
 * **The opaque list is reversed by the same three lines and is deliberately not
 * touched here.** Its `renderOrder = -1` users (the sky dome, ground shadows,
 * crash marks) are equally inverted, but they are depth-write-free decals whose
 * ordering is the ground-plate lane's subject (`three/plate-paint-order.ts`),
 * and changing both at once would let either fix mask the other. Recorded in
 * `docs/engineering/lessons.md` rather than fixed in passing.
 */

/**
 * One entry of three's transparent render list — the fields
 * `reversePainterSortStable` reads. Declared structurally rather than imported
 * because three does not export the render-item type.
 */
export interface TransparentRenderItem {
  /**
   * All four are `number | null` because that is exactly how three declares
   * `RenderItem` — its render items are pooled and reused, so every field is
   * nullable in the type even though a *pushed* item always has numbers in it.
   * Three's own comparator subtracts them regardless and lets JS coerce `null`
   * to `0`; the `?? 0`s below are that same arithmetic, written so the type
   * checker can see it. Matching the declaration rather than narrowing it is
   * what lets `applyTransparentSort` take a real `WebGPURenderer`.
   */
  readonly groupOrder: number | null;
  readonly renderOrder: number | null;
  readonly z: number | null;
  readonly id: number | null;
}

/**
 * The comparator to hand `renderer.setTransparentSort()` **when and only when
 * the renderer is running a reversed depth buffer**.
 *
 * Keys, in order: `groupOrder` descending, `renderOrder` descending, `z`
 * descending, `id` ascending. The first two are negated against three's own
 * comparator so that the `reverse()` which follows leaves them ascending; the
 * last two are byte-for-byte three's, so the depth ordering is untouched.
 *
 * Every key is a total tiebreak down to `id`, so no two items ever compare
 * equal and the reversal is exact rather than stability-dependent.
 */
export function reversedDepthTransparentSort(a: TransparentRenderItem, b: TransparentRenderItem): number {
  if (a.groupOrder !== b.groupOrder) return (b.groupOrder ?? 0) - (a.groupOrder ?? 0);
  if (a.renderOrder !== b.renderOrder) return (b.renderOrder ?? 0) - (a.renderOrder ?? 0);
  if (a.z !== b.z) return (b.z ?? 0) - (a.z ?? 0);
  return (a.id ?? 0) - (b.id ?? 0);
}

/** What `setTransparentSort` wants: three types the setter as taking any two render items. */
type TransparentSortMethod = (a: TransparentRenderItem, b: TransparentRenderItem) => number;

/** The subset of `THREE.WebGPURenderer` this module drives, so the rule is testable without a GPU. */
export interface TransparentSortTarget {
  /**
   * three's **own** record of whether the depth buffer actually came up
   * reversed — not the flag it was asked for. The two differ: the WebGL
   * fallback overwrites this to `false` at backend-init time when
   * `EXT_clip_control` is missing, which is exactly the `?renderer=webgl`
   * capture hatch.
   */
  readonly reversedDepthBuffer: boolean;
  setTransparentSort(method: TransparentSortMethod | null): void;
}

/**
 * Installs the ladder-preserving sort, or clears it — **after
 * `renderer.init()`**, because that is when the backend has finished deciding
 * whether reversed depth is actually on.
 *
 * Keyed on the renderer's own state rather than on `REVERSED_DEPTH_BUFFER`,
 * and the distinction is load-bearing rather than fastidious: under
 * `/fly?renderer=webgl` on a context without `EXT_clip_control` three does not
 * reverse the list at all, so installing the inverting comparator there would
 * flip the ladder in the *other* direction and break the one capture path this
 * bug was found through. Reading the flag three settled on makes the override
 * exactly as true as the condition it compensates for.
 */
export function applyTransparentSort(renderer: TransparentSortTarget): void {
  renderer.setTransparentSort(renderer.reversedDepthBuffer ? reversedDepthTransparentSort : null);
}
